import { PageIndex, PageScrollPercentage } from "../state/page";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { getStackFromUrl } from "@quickpoint.me/shared";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import gsap from "gsap";
import { useLayoutEffect } from "react";
import { ParsedCode } from "../state/selectors";

// a /PAGE/N URL should open scrolled to that page. Captured at module load:
// by the time this component mounts, QpHistory has already rewritten the URL.
let initialUrlPage = getStackFromUrl(window.location.pathname).page;

gsap.registerPlugin(ScrollTrigger);

function ScrollPageIndexUpdater() {
	const setPageIndex = useSetRecoilState(PageIndex);
	const setPageScrollPercentage = useSetRecoilState(PageScrollPercentage);
	const parsedCode = useRecoilValue(ParsedCode);
	const totalPages = parsedCode.length;

	useLayoutEffect(() => {
		// Wrap the GSAP setup in a timeout to prevent race conditions with React's Strict Mode.
		// This ensures the cleanup from the first render pass is complete before the setup of the second pass begins.

		const scrollerEl = document.querySelector("#scroll-elements-container");

		if (!scrollerEl) {
			return;
		}

		const ctx = gsap.context(() => {
			setPageIndex(1);
			setPageScrollPercentage(0);

			for (let i = 1; i <= totalPages; i++) {
				const triggerEl = document.querySelector(`#scroll-page-${i}`);
				if (triggerEl) {
					ScrollTrigger.create({
						trigger: triggerEl,
						scroller: scrollerEl,
						start: "top center",
						end: "bottom center",
						scrub: true,
						onToggle: (self) => {
							if (self.isActive) {
								setPageIndex(i);
							}
						},
						onUpdate: (self) => {
							if (self.isActive) {
								setPageScrollPercentage(self.progress);
							}
						},
					});
				}
			}

			const refreshTimeout = setTimeout(() => {
				ScrollTrigger.refresh();

				const initialPage = initialUrlPage;
				if (initialPage && initialPage > 1 && totalPages > 0) {
					initialUrlPage = false;
					const clamped = Math.min(initialPage, totalPages);
					const target = document.querySelector<HTMLElement>(
						`#scroll-page-${clamped}`,
					);
					if (target) {
						const scroller = scrollerEl as HTMLElement;
						scroller.scrollTop =
							target.getBoundingClientRect().top -
							scroller.getBoundingClientRect().top +
							scroller.scrollTop;
					}
				}
			}, 100);

			// Return a cleanup function for the context
			return () => clearTimeout(refreshTimeout);
		});

		// Return a cleanup function for the main effect
		return () => ctx.revert();

		// Cleanup for the initial timeout
	}, [totalPages, setPageIndex, setPageScrollPercentage]);

	return null;
}

export default ScrollPageIndexUpdater;
